import { CardContainer } from "@/components/commons/CardContainer";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { TDataRegistrationStats } from "@/features/learning-content/types/learningContent";
import { divider, text } from "@/theme/colors";
import { Box, Stack, Typography, styled } from "@mui/material";
import { FC } from "react";

// Level stat configuration with label and unit
const LEVEL_STATS = [
  { key: "vocabularyCount", label: "Lv.0 単語学習", unit: "語" },
  { key: "difficulty1QuestionCount", label: "Lv.1 単語理解", unit: "問" },
  { key: "difficulty2QuestionCount", label: "Lv.2 初級問題数", unit: "問" },
  { key: "difficulty3QuestionCount", label: "Lv.3 中級問題数", unit: "問" },
  { key: "difficulty4QuestionCount", label: "Lv.4 上級問題数", unit: "問" },
  { key: "difficulty5QuestionCount", label: "Lv.5 実践問題数", unit: "問" },
] as const;

type TDataRegistrationStatsProps = {
  stats: TDataRegistrationStats;
};

// Displays current data registration statistics matching the design
export const DataRegistrationStats: FC<TDataRegistrationStatsProps> = ({ stats }) => {
  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          現在のデータ登録数
        </Typography>

        {/* Level stats row */}
        <StatsRow>
          {LEVEL_STATS.map(({ key, label, unit }) => (
            <StatItem key={key}>
              <StatLabel>{label}</StatLabel>
              <StatValueRow>
                <StatNumber>{stats[key]}</StatNumber>
                <StatUnit>{unit}</StatUnit>
              </StatValueRow>
            </StatItem>
          ))}
        </StatsRow>

        {/* Meta info row - 4 items horizontally */}
        <MetaRow>
          <MetaItem>
            <ItemInfo label="単語の最終更新者" value={stats.vocabularyLastUpdater} />
          </MetaItem>
          <MetaItem>
            <ItemInfo label="単語の最終更新日" value={stats.vocabularyLastUpdatedAt} />
          </MetaItem>
          <MetaItem>
            <ItemInfo label="問題の最終更新者" value={stats.questionLastUpdater} />
          </MetaItem>
          <MetaItem>
            <ItemInfo label="問題の最終更新日" value={stats.questionLastUpdatedAt} />
          </MetaItem>
        </MetaRow>
      </Stack>
    </CardContainer>
  );
};

const StatsRow = styled(Box)`
  display: flex;
  align-items: stretch;
`;

const StatItem = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled(Typography)`
  font-size: 12px;
  color: ${text.secondary};
  line-height: 1.4;
`;

const StatValueRow = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px dashed ${divider.high};
  padding-bottom: 8px;
  margin-right: 16px;
`;

const StatNumber = styled(Typography)`
  font-size: 14px;
  font-weight: 700;
  color: ${text.primary};
  line-height: 1.2;
`;

const StatUnit = styled(Typography)`
  font-size: 14px;
  color: ${text.secondary};
  font-weight: 500;
`;

const MetaRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;

const MetaItem = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
